import { CountryInfo, MarketplaceInfo } from 'pages/insights-pro/marketplace-tab/model';
import { MetricsChart } from 'components/MetricsChart';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { ChartData } from 'components/chart/data/ChartData';
import { BarChartRenderer } from 'components/chart/renderer/BarChartRenderer';
import { BarChartConfig } from 'components/chart/data/BarChartConfig';
import { getIsOtherAwareArray } from 'pages/insights-pro/marketplace-tab/components/getArrayWithIsOther';
import { useIntl } from 'react-intl';
import { getArrayWithIsOthers } from 'pages/insights-pro/common/graphs/getArrayWithIsOthers';
import { TileContent } from 'components/Layout/PageContainer';
import { Measure } from 'services/model/model.service';
import { EmptyState } from 'components/EmptyState';

interface CountriesMarketplaceSharesProps {
  countries: CountryInfo[];
  marketplaces: MarketplaceInfo[];
  gmvMeasure: Measure;
}

export function CountriesMarketplaceShares({ countries, marketplaces, gmvMeasure }: CountriesMarketplaceSharesProps) {
  const intl = useIntl();
  if (countries.length === 0) {
    return <EmptyState />;
  }
  const createMarketplacesChartConfig = () => {
    const marketplacesChartConfig = new BarChartConfig();
    marketplacesChartConfig.breakdownMode = 'fill-stacked';
    marketplacesChartConfig.value = {
      code: gmvMeasure.code,
      label: gmvMeasure.metric.name,
      unit: gmvMeasure.metric.unit,
    };
    marketplacesChartConfig.legendConfig = {
      hide: false,
    };
    // I have no clue why we need to re-sort but this goes randomly haywire in the browser when passed in
    const reversedCountries = countries
      .sort((a, b) => b.totalGmv - a.totalGmv)
      .slice(0, 5)
      .reverse();
    marketplacesChartConfig.data = [
      new ChartDataArray(
        ChartDataType.primary,
        'all',
        reversedCountries.map(country => new ChartData(country.name, country.totalGmv))
      ),
      ...getArrayWithIsOthers(marketplaces, [gmvMeasure.code], intl).map(marketplace => {
        const array = new ChartDataArray(
          ChartDataType.breakdown,
          marketplace.name,
          reversedCountries.map(country => {
            const countryMarketplace = getIsOtherAwareArray(country.marketplaces, [gmvMeasure.code], intl).find(
              cm => cm.name === marketplace.name
            );
            return new ChartData(country.name, gmvMeasure.getValue(countryMarketplace?.entry.values) ?? 0);
          })
        );
        array.color = marketplace.color;
        return array;
      }),
    ];

    return marketplacesChartConfig;
  };

  const marketplacesChartRenderer = new BarChartRenderer(createMarketplacesChartConfig());

  return (
    <TileContent className="px-0">
      <MetricsChart renderer={marketplacesChartRenderer} height="h-[400px]" />
    </TileContent>
  );
}
